"use client"

/**
 * /agencies — the agency solutions page (Website Final Developer Master,
 * 4 Aug 2026, Task 7). Runs on the shared dark editorial system: global
 * SiteHeader/SiteFooter + globals.css tokens (velova-display, velova-block,
 * velova-btn), same motion primitives as the homepage. No client proof —
 * the page argues the operating model, not a logo wall.
 */

import Link from "next/link"
import { ArrowRight, ArrowUpRight } from "lucide-react"
import { SiteHeader } from "@/components/landing/site-header"
import { SiteFooter } from "@/components/landing/site-footer"
import {
  FadeUp,
  Stagger,
  StaggerItem,
  MagneticBtn,
  ScrollProgress,
  ParallaxY,
} from "@/components/landing/motion-primitives"
import { PLANS } from "@/lib/site-config"

const SCALE_PLAN = PLANS.find((p) => p.id === "scale")!

export default function AgenciesPage() {
  return (
    <main className="velova-canvas min-h-screen overflow-x-clip">
      <ScrollProgress />
      <SiteHeader />
      <Hero />
      <WinTheConversation />
      <OnboardTheMarket />
      <RunTheWork />
      <ProveTheService />
      <ScaleWithoutFlattening />
      <FinalCTA />
      <SiteFooter />
    </main>
  )
}

/* ─── Section kicker — § number + hairline + label, matches the
   editorial rhythm used across the redesigned marketing pages. ────── */
function SectionKicker({ num, label }: { num: string; label: string }) {
  return (
    <StaggerItem className="col-span-12 md:col-span-4 flex items-center gap-4">
      <span className="velova-mono-label opacity-60">{"§"} {num}</span>
      <div className="flex-1 h-px bg-[var(--velova-line-strong)]" />
      <span className="velova-mono-label">{label}</span>
    </StaggerItem>
  )
}

/* ─── Hero ──────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="relative pt-16 md:pt-24 pb-14 md:pb-20 overflow-hidden">
      <ParallaxY range={40} className="absolute inset-x-0 top-0 h-full pointer-events-none">
        <div className="velova-tint h-full w-full" />
      </ParallaxY>
      <div className="relative max-w-none mx-auto px-4 sm:px-8 lg:px-12">
        <FadeUp y={10}>
          <span className="velova-mono-label" style={{ color: "var(--velova-blue-soft)" }}>
            For agencies
          </span>
        </FadeUp>

        <FadeUp delay={0.1} y={24} className="mt-5">
          <h1 className="velova-display text-[34px] sm:text-[48px] md:text-[64px] lg:text-[76px] leading-[1.05] max-w-4xl">
            Turn AI search into a service{" "}
            <span className="velova-italic" style={{ color: "var(--velova-blue)" }}>
              clients can see.
            </span>
          </h1>
        </FadeUp>

        <FadeUp delay={0.2} className="mt-8 max-w-[680px]">
          <p className="text-[16px] sm:text-[18px] leading-[1.6] text-white/70">
            Benchmark every client, show exactly where competitors win, run
            the work and report what changed—all from one multi-brand
            workspace.
          </p>
        </FadeUp>

        <FadeUp delay={0.3} className="mt-9 flex flex-wrap gap-3">
          <MagneticBtn>
            <Link href="/signup" className="velova-btn velova-btn-ink">
              Start free
              <ArrowRight className="w-[14px] h-[14px] velova-btn-arrow" />
            </Link>
          </MagneticBtn>
          <Link href="/contact" className="velova-btn velova-btn-ghost">
            Talk to us
          </Link>
        </FadeUp>
      </div>
    </section>
  )
}

/* ─── § 01 — Win the conversation ───────────────────────────────── */
function WinTheConversation() {
  return (
    <section className="py-16 sm:py-24 md:py-28">
      <div className="max-w-none mx-auto px-4 sm:px-8 lg:px-12">
        <Stagger className="grid grid-cols-12 gap-8 mb-10 md:mb-12">
          <SectionKicker num="01" label="Win the conversation" />
          <StaggerItem className="col-span-12 md:col-span-8 velova-display text-[26px] sm:text-[36px] md:text-[48px] leading-[1.08]">
            Start the pitch with the answer,{" "}
            <span className="velova-italic" style={{ color: "var(--velova-blue)" }}>
              not a trend deck.
            </span>
          </StaggerItem>
        </Stagger>

        <FadeUp className="max-w-[720px]" y={16}>
          <p className="text-[15px] sm:text-[17px] leading-[1.65] text-white/70">
            Show a prospect the buyer questions where the brand is absent,
            the competitors already present and the sources shaping the
            category. The opportunity becomes concrete before the proposal
            begins.
          </p>
        </FadeUp>
      </div>
    </section>
  )
}

/* ─── § 02 — Onboard the market ─────────────────────────────────── */
const WORKSPACE_LABELS = [
  "Client",
  "Brand",
  "Market",
  "Language",
  "Competitors",
  "Plan usage",
]

function OnboardTheMarket() {
  return (
    <section className="py-16 sm:py-24 md:py-28 bg-[var(--velova-paper-2)] border-y border-[var(--velova-line-strong)]">
      <div className="max-w-none mx-auto px-4 sm:px-8 lg:px-12">
        <Stagger className="grid grid-cols-12 gap-8 mb-10 md:mb-12">
          <SectionKicker num="02" label="Onboard the market" />
          <StaggerItem className="col-span-12 md:col-span-8 velova-display text-[26px] sm:text-[36px] md:text-[48px] leading-[1.08]">
            A separate operating view{" "}
            <span className="velova-italic" style={{ color: "var(--velova-blue)" }}>
              for every client.
            </span>
          </StaggerItem>
        </Stagger>

        <FadeUp className="max-w-[720px] mb-9" y={16}>
          <p className="text-[15px] sm:text-[17px] leading-[1.65] text-white/70">
            Keep brands, markets, languages, competitors and buyer-question
            sets distinct. Switch between clients without mixing the context
            that makes each answer meaningful.
          </p>
        </FadeUp>

        <Stagger className="flex flex-wrap gap-2.5" amount={0.3}>
          {WORKSPACE_LABELS.map((label) => (
            <StaggerItem key={label}>
              <span className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.03] px-4 py-2 text-[13px] font-medium text-white/80">
                {label}
              </span>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  )
}

/* ─── § 03 — Run the work ───────────────────────────────────────── */
function RunTheWork() {
  return (
    <section className="py-16 sm:py-24 md:py-28">
      <div className="max-w-none mx-auto px-4 sm:px-8 lg:px-12">
        <Stagger className="grid grid-cols-12 gap-8 mb-10 md:mb-12">
          <SectionKicker num="03" label="Run the work" />
          <StaggerItem className="col-span-12 md:col-span-8 velova-display text-[26px] sm:text-[36px] md:text-[48px] leading-[1.08]">
            Move from report to delivery{" "}
            <span className="velova-italic" style={{ color: "var(--velova-blue)" }}>
              in the same meeting.
            </span>
          </StaggerItem>
        </Stagger>

        <FadeUp className="max-w-[720px]" y={16}>
          <p className="text-[15px] sm:text-[17px] leading-[1.65] text-white/70">
            Turn gaps into content, comparison pages, FAQs, schema and
            outreach. Assign the action, refine the asset, publish it and
            keep the status visible to the team.
          </p>
        </FadeUp>
      </div>
    </section>
  )
}

/* ─── § 04 — Prove the service ──────────────────────────────────── */
function ProveTheService() {
  return (
    <section className="py-16 sm:py-24 md:py-28 bg-[var(--velova-paper-2)] border-y border-[var(--velova-line-strong)]">
      <div className="max-w-none mx-auto px-4 sm:px-8 lg:px-12">
        <Stagger className="grid grid-cols-12 gap-8 mb-10 md:mb-12">
          <SectionKicker num="04" label="Prove the service" />
          <StaggerItem className="col-span-12 md:col-span-8 velova-display text-[26px] sm:text-[36px] md:text-[48px] leading-[1.08]">
            Report movement,{" "}
            <span className="velova-italic" style={{ color: "var(--velova-blue)" }}>
              not activity.
            </span>
          </StaggerItem>
        </Stagger>

        <FadeUp className="max-w-[720px]" y={16}>
          <p className="text-[15px] sm:text-[17px] leading-[1.65] text-white/70">
            Show how the answer changed, which actions went live and what
            GSC and GA recorded afterward. Give clients a clear view from
            work delivered to visibility, traffic, conversion and revenue
            signals.
          </p>
        </FadeUp>
      </div>
    </section>
  )
}

/* ─── § 05 — Scale without flattening clients ───────────────────── */
function ScaleWithoutFlattening() {
  return (
    <section className="py-16 sm:py-24 md:py-28">
      <div className="max-w-none mx-auto px-4 sm:px-8 lg:px-12">
        <Stagger className="grid grid-cols-12 gap-8 mb-10 md:mb-12">
          <SectionKicker num="05" label="Scale without flattening clients" />
          <StaggerItem className="col-span-12 md:col-span-8 velova-display text-[26px] sm:text-[36px] md:text-[48px] leading-[1.08]">
            One system.{" "}
            <span className="velova-italic" style={{ color: "var(--velova-blue)" }}>
              Separate strategies.
            </span>
          </StaggerItem>
        </Stagger>

        <FadeUp className="max-w-[720px] mb-9" y={16}>
          <p className="text-[15px] sm:text-[17px] leading-[1.65] text-white/70">
            Use a repeatable operating model across the roster while
            preserving each client&apos;s market, language, competitors,
            approval flow and commercial goals.
          </p>
        </FadeUp>

        <FadeUp delay={0.1} className="max-w-[620px]">
          <div className="velova-block velova-block-blue px-5 py-4">
            <p className="text-[14px] sm:text-[15px] leading-[1.65] text-white/80">
              <span className="text-white font-medium">Scale</span> supports{" "}
              {SCALE_PLAN.brands.toLowerCase()} brands. Enterprise supports
              larger agency networks and configured coverage.
            </p>
          </div>
        </FadeUp>

        <FadeUp delay={0.2} className="mt-6">
          <Link
            href="/pricing"
            className="inline-flex items-center gap-2 text-[14px] font-medium text-[var(--velova-blue-soft)] hover:opacity-70 transition-opacity"
          >
            Compare plans
            <ArrowUpRight className="w-[14px] h-[14px]" />
          </Link>
        </FadeUp>
      </div>
    </section>
  )
}

/* ─── Final CTA ──────────────────────────────────────────────────── */
function FinalCTA() {
  return (
    <section className="py-16 sm:py-24 md:py-36 relative overflow-hidden">
      <ParallaxY range={30} className="absolute inset-0 pointer-events-none">
        <div className="velova-tint opacity-70 h-full w-full" />
      </ParallaxY>
      <div className="relative max-w-none mx-auto px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-12 gap-8 items-end">
          <FadeUp className="col-span-12 md:col-span-8" y={28}>
            <h2 className="velova-display text-[32px] sm:text-[48px] md:text-[64px] leading-[1.06]">
              Build your agency&apos;s{" "}
              <span className="velova-italic" style={{ color: "var(--velova-blue)" }}>
                AI-search service
              </span>{" "}
              on Velova.
            </h2>
            <p className="mt-6 text-[15px] sm:text-[17px] leading-[1.6] text-white/65 max-w-xl">
              Start with one client. Add the markets and brands as the
              service grows.
            </p>
          </FadeUp>
          <FadeUp className="col-span-12 md:col-span-4 md:pb-2" delay={0.15}>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <MagneticBtn>
                <Link
                  href="/signup"
                  className="velova-btn velova-btn-ink justify-center w-full sm:w-auto"
                >
                  Start free
                  <ArrowRight className="w-[14px] h-[14px] velova-btn-arrow" />
                </Link>
              </MagneticBtn>
              <Link
                href="/contact"
                className="velova-btn velova-btn-ghost justify-center w-full sm:w-auto"
              >
                Talk to us
              </Link>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  )
}
